import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useMessages } from '@zenbusiness/application-commons-intl';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import BusinessInfoFieldset from '#client/components/BusinessInfoFieldset';
import ConstructorCallout from '#client/components/ConstructorCallout';
import CtaContainer from '#client/components/CtaContainer';
import Form from '#client/components/Form';
import IndustryFieldset from '#client/components/IndustryFieldset';
import NumberedSection from '#client/components/NumberedSection';
import useFlowState from '#client/hooks/flow/useFlowState';
import useUpdateCart from '#client/hooks/useUpdateCart';
import { formErrorsToTrackingErrorsMapper } from '#client/utils/formErrorsToTrackingErrorsMapper';

import { useQuestions } from '../../hooks/useQuestions';
import BusinessExperienceFieldset from '../BusinessExperienceFieldset';
import BusinessStageFieldset from '../BusinessStageFieldset';

const MESSAGE_KEY = 'shopping.page.questions.questionsForm';

const MESSAGE_IDS = {
  callout: {
    title: `${MESSAGE_KEY}.callout.title`,
    description: `${MESSAGE_KEY}.callout.description`
  },
  experience: { title: `${MESSAGE_KEY}.businessExperienceSection.title` },
  industry: {
    title: `${MESSAGE_KEY}.businessIndustrySection.title`,
    description: `${MESSAGE_KEY}.businessIndustrySection.description`
  },
  info: { title: `${MESSAGE_KEY}.businessInfoSection.title` },
  stage: {
    title: `${MESSAGE_KEY}.businessStageSection.title`,
    description: `${MESSAGE_KEY}.businessStageSection.description`
  },
  buttonText: `${MESSAGE_KEY}.buttonText`,
  required: `${MESSAGE_KEY}.required`
};

const HighlightText = (text) => (
  <Text color="blue" component="span">
    {text}
  </Text>
);

function QuestionForms({ formationCountyStates }) {
  const { callout, experience, industry, info, stage, buttonText, required } = useMessages(
    MESSAGE_IDS,
    {
      highlight: HighlightText
    }
  );

  const { formState } = useFormContext();
  const { formMethods, onSubmit } = useQuestions();
  const captureException = useCaptureException();
  const trackFormSubmitted = useClickstreamCallback('formSubmitted');
  const trackFormSubmissionFailed = useClickstreamCallback('formSubmissionFailed');
  const { businessInfo } = useFlowState();
  const updateCart = useUpdateCart();

  const { isSubmitting } = formState;

  const submitErrorHandler = (_errors) => {
    trackFormSubmissionFailed({ formErrors: formErrorsToTrackingErrorsMapper(_errors) });
  };

  const submitHandler = async (data) => {
    if (businessInfo.businessEntityUuid) {
      onSubmit(data);
      return;
    }
    const cartIsUpdated = await updateCart({
      name: data.name,
      formationCounty: data.formationCounty,
      jurisdictionState: data.jurisdictionState,
      industry: data.industry,
      priorExperience: data.businessExperience,
      launchStatus: data.businessStage,
      entityType: businessInfo.entityType
    });
    if (cartIsUpdated) {
      onSubmit(data);
    }
  };

  const handleFormSubmit = async (e) => {
    try {
      await trackFormSubmitted({
        elementName: 'questions_form',
        dynamicProperties: {
          domestic_jurisdiction: formMethods.getValues('jurisdictionState')
        }
      });

      await formMethods.handleSubmit(submitHandler, submitErrorHandler)(e);
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <Form aria-label="Questions" gap={4} onSubmit={handleFormSubmit}>
      <Stack gap={3}>
        <Text>{required}</Text>
        <NumberedSection sectionNumber={1} {...info}>
          <BusinessInfoFieldset
            fieldsConfig={{
              businessName: {
                autoFocus: true,
                show: true,
                'data-testid': 'business-info-name',
                messageKey: MESSAGE_KEY
              },
              jurisdictionState: {
                show: true,
                'data-testid': 'business-info-jurisdiction-state'
              },
              formationCounty: {
                show: true,
                formationCountyStates,
                'data-testid': 'business-info-county'
              }
            }}
          />
        </NumberedSection>
        <ConstructorCallout {...callout} />
      </Stack>
      <NumberedSection sectionNumber={2} {...industry}>
        <IndustryFieldset
          fieldsConfig={{
            industry: {
              'data-testid': 'industry-field',
              validation: {
                required: null
              }
            }
          }}
        />
      </NumberedSection>
      <NumberedSection sectionNumber={3} {...stage}>
        <BusinessStageFieldset />
      </NumberedSection>
      <NumberedSection sectionNumber={4} {...experience}>
        <BusinessExperienceFieldset />
      </NumberedSection>
      <CtaContainer marginBlockStart={0}>
        <Button
          clickstreamProps={{ elementName: 'questions_form_submit' }}
          data-testid="questions-form-submit"
          fullWidth
          loading={isSubmitting}
          type="submit"
        >
          {buttonText}
        </Button>
      </CtaContainer>
    </Form>
  );
}

QuestionForms.propTypes = {
  formationCountyStates: PropTypes.instanceOf(Set)
};

export default QuestionForms;
